/**
 * What this browser remembers about basemaps: the operator's chosen one, and whether
 * this deployment can serve imagery at all.
 *
 * Its own module, with NO imports, because both ends of it are read from places that
 * must not pull the map in. `useAuth` writes the availability flag and is rendered by
 * every page and mocked by dozens of tests; `droneMapTypes` calls `getApiUrl()` at
 * module scope, so an auth test that partially mocks `@/lib/api` fails to even load if
 * auth reaches the map through it. Storage here, style resolution in
 * `droneMapReducer` where `STYLES` already lives.
 */

/**
 * Where the operator's basemap choice lives.
 *
 * ONE key, not one per display mode. D3 made the basemap follow DAY/NIGHT and stored a
 * separate choice under each, so switching mode swapped the ground out from under a
 * picture the operator had already set - reported as a bug, because that is what it is
 * from the operator's side: the mode is how bright the CHROME is, the basemap is what
 * the map is OF. Two decisions, two controls, no coupling. The mode-scoped keys are
 * retired rather than migrated (as D13 retired the key before them): a choice made
 * under "the mode picks the basemap" carries no meaning once it does not.
 */
export const MAP_STYLE_STORAGE_KEY = 'basemap_style';

/** The per-mode keys of D3, and the single key that preceded them. Never read, removed once. */
const RETIRED_MAP_STYLE_KEYS = ['map_style', 'map_style:light', 'map_style:dark'];

/**
 * Whether this deployment can serve imagery, cached from the last `/api/auth/me`.
 *
 * `getPersistedMapStyle` is synchronous and is called by six maps as they are
 * constructed, while `features.satellite_basemap` arrives asynchronously and only the
 * COP subscribes to it. Caching the last answer is what lets imagery be the DEFAULT
 * rather than merely an option: a deployment with no `GOOGLE_MAPS_API_KEY` answers 501
 * for the style document, so defaulting to it unconditionally would blank every map on
 * that server. Absent (a profile that has never signed in here) reads as "cannot", so
 * the first paint of a brand-new profile is the dark map and never a blank one.
 */
export const SATELLITE_AVAILABLE_STORAGE_KEY = 'basemap_satellite_available';

/** Whether imagery may be defaulted to. Written by `useAuth` on every `/api/auth/me`. */
export function isSatelliteAvailable(): boolean {
  try {
    return localStorage.getItem(SATELLITE_AVAILABLE_STORAGE_KEY) === 'true';
  } catch {
    // A store we cannot read tells us nothing; the safe answer is "no imagery".
    return false;
  }
}

export function rememberSatelliteAvailable(available: boolean): void {
  try {
    localStorage.setItem(SATELLITE_AVAILABLE_STORAGE_KEY, available ? 'true' : 'false');
  } catch {
    // This session still works; the next one opens on the fallback instead.
  }
}

/** True once the operator has picked a basemap by hand; until then the default applies
 *  and may still be updated under them (see `DroneMap`'s imagery effect). */
export function hasStoredMapStyle(): boolean {
  try {
    return localStorage.getItem(MAP_STYLE_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** The raw stored value, unvalidated - the caller owns the list of styles that exist. */
export function readStoredMapStyle(): string | null {
  try {
    return localStorage.getItem(MAP_STYLE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** The operator chose a basemap; it is theirs in both display modes, until they change it. */
export function writeStoredMapStyle(style: string): void {
  try {
    localStorage.setItem(MAP_STYLE_STORAGE_KEY, style);
  } catch {
    // The style still applies for this session; it just will not persist.
  }
}

/** Never throws: localStorage can (privacy mode, a locked-down browser policy). */
export function retireLegacyMapStyleKeys(): void {
  try {
    for (const key of RETIRED_MAP_STYLE_KEYS) localStorage.removeItem(key);
  } catch {
    // A store we cannot write has nothing to retire; the default applies.
  }
}
